import type { GroupingField, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { ItemDataType } from "../../../../../../../../api/types/svTypes.ts";
import { isAggregation, isMeasure, isNotAggregatedDimension } from "../../../../utilities/fieldsHelper.ts";
import { isReferenceDateField } from "./isReferenceDateField.ts";

export const getAvailableGroupFields = (values: ReportField[], groupAreaValues: GroupingField[]) => {
  const existingFieldNames = new Set<string>();

  groupAreaValues.forEach((groupField) => {
    const matchingField = values.find((f) => f.config.guid === groupField.id);
    if (matchingField) {
      existingFieldNames.add(matchingField.meta.name);
    }
  });

  // Mirror of canDropItem rule: when a reference-date field is present in the report fields,
  // measures cannot be used as grouping keys (they would conflict with the reference date).
  // Aggregated reference-date fields (e.g. "Count of PostingDate") do not count — they are
  // treated as regular aggregations, not as the report's reference date anchor.
  const hasReferenceDateField = values.some((f) => isReferenceDateField(f.meta) && !isAggregation(f));

  return values.filter((f): f is ReportField => {
    const isAlreadyInGroupArea = groupAreaValues.some((v) => v.id === f.config.guid);
    if (isAlreadyInGroupArea) {
      return false;
    }

    const hasNameCollision = existingFieldNames.has(f.meta.name);
    if (hasNameCollision) {
      return false;
    }

    // Scalar measures (including Date-typed ones) are valid grouping keys as long as
    // there is no non-aggregated reference-date dimension in the report.
    const isGroupableScalarMeasure = isMeasure(f) && f.meta.capabilities.isScalar && !hasReferenceDateField;
    if (isGroupableScalarMeasure) {
      return true;
    }

    // Date-typed dimensions are only allowed when they are the report's reference date.
    const isUnsupportedDateField = f.meta.type === ItemDataType.Date && !isReferenceDateField(f.meta);
    if (isUnsupportedDateField) {
      return false;
    }

    return isNotAggregatedDimension(f);
  });
};
